import { useState } from 'react'
import type { FormEvent } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'

import { Alert, Button, Panel, TextField } from '../components/ui'
import { ApiError, ContractError, NetworkError } from '../api/client'
import { t } from '../i18n'
import { useSession } from '../auth/session'
import type { SignInRedirectState } from '../auth/guards'

function messageFor(error: unknown): string {
  // The server's own words, rendered in Russian where we recognise them. The
  // sign-in failure stays as vague as the server made it: it will not say whether
  // the account exists, and repairing that "helpfully" here would turn the form
  // into a way of finding out who works at the agency.
  if (error instanceof ApiError) return error.message
  if (error instanceof NetworkError) return t('errors.network')
  if (error instanceof ContractError) return t('errors.badResponse')
  return t('errors.unexpected')
}

export function SignIn() {
  const { status, signIn, isSigningIn } = useSession()
  const location = useLocation()
  const navigate = useNavigate()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [touched, setTouched] = useState(false)

  const target = (location.state as SignInRedirectState | null)?.from ?? '/inbox'

  // Already signed in — e.g. arrived here by typing the URL, or came back after
  // signing in on another tab.
  if (status === 'authenticated') {
    return <Navigate to={target} replace />
  }

  const emailMissing = touched && email.trim() === ''
  const passwordMissing = touched && password === ''

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setTouched(true)
    setError(null)

    if (email.trim() === '' || password === '') return

    try {
      await signIn({ email: email.trim(), password })
      navigate(target, { replace: true })
    } catch (cause) {
      setError(messageFor(cause))
    }
  }

  return (
    <div className="centered">
      <Panel className="signin__card">
        <div className="signin__brand">
          <h1 className="signin__title">{t('app.name')} — {t('signIn.title')}</h1>
          <p className="signin__subtitle">{t('signIn.subtitle')}</p>
        </div>

        {error !== null ? <Alert tone="error">{error}</Alert> : null}

        <form className="signin__form" onSubmit={handleSubmit} noValidate>
          <TextField
            label={t('signIn.email')}
            type="email"
            name="email"
            autoComplete="username"
            autoFocus
            placeholder={t('signIn.emailPlaceholder')}
            value={email}
            error={emailMissing ? t('signIn.emailRequired') : undefined}
            onChange={(event) => setEmail(event.target.value)}
          />

          <TextField
            label={t('signIn.password')}
            type="password"
            name="password"
            autoComplete="current-password"
            value={password}
            error={passwordMissing ? t('signIn.passwordRequired') : undefined}
            onChange={(event) => setPassword(event.target.value)}
          />

          <Button type="submit" block busy={isSigningIn}>
            {isSigningIn ? t('signIn.submitting') : t('signIn.submit')}
          </Button>
        </form>
      </Panel>
    </div>
  )
}
